package com.evoapps.evoshift.di

import com.evoapps.evoshift.push.DeviceTokenSync
import com.evoapps.evoshift.viewmodels.NotificationsViewModel
import com.evoapps.evoshift.widget.platformShiftWidgetStore
import com.evoapps.evoshift.widget.platformWatchStore
import com.evoapps.evoshift.widget.WatchClockHandler
import com.evoapps.evoshift.widget.ShiftWidgetSync
import com.evoapps.evoshift.Route
import com.evoapps.evoshift.data.di.dataModule
import com.evoapps.evoshift.domain.di.domainModule
import com.evoapps.evoshift.session.WorkplaceSessionManager
import com.evoapps.evoshift.session.WorkplaceMonthSelection
import com.evoapps.evoshift.session.platformLastWorkplaceStore
import com.evoapps.evoshift.location.LocationNoticeStore
import com.evoapps.evoshift.location.platformLocationNoticeStore
import com.evoapps.evoshift.observability.AppTelemetry
import com.evoapps.evoshift.observability.ConsentStore
import com.evoapps.evoshift.observability.GatedTelemetry
import com.evoapps.evoshift.observability.PrivacyConsent
import com.evoapps.evoshift.observability.Telemetry
import com.evoapps.evoshift.observability.TelemetryPlatform
import com.evoapps.evoshift.observability.platformConsentStore
import com.evoapps.evoshift.viewmodels.PrivacyChoicesViewModel
import com.evoapps.evoshift.viewmodels.AbsencesViewModel
import com.evoapps.evoshift.viewmodels.RequestsViewModel
import com.evoapps.evoshift.viewmodels.AuthViewModel
import com.evoapps.evoshift.viewmodels.AvailabilityFormViewModel
import com.evoapps.evoshift.viewmodels.AvailabilityViewModel
import com.evoapps.evoshift.viewmodels.CreateRosterViewModel
import com.evoapps.evoshift.viewmodels.DailyRecordsViewModel
import com.evoapps.evoshift.viewmodels.DashboardViewModel
import com.evoapps.evoshift.viewmodels.EmployeeDashboardViewModel
import com.evoapps.evoshift.viewmodels.EmployeeFormViewModel
import com.evoapps.evoshift.viewmodels.FinancialsViewModel
import com.evoapps.evoshift.viewmodels.MyShiftClock
import com.evoapps.evoshift.viewmodels.PayrollViewModel
import com.evoapps.evoshift.viewmodels.RosterCapacityViewModel
import com.evoapps.evoshift.viewmodels.ReportPrintViewModel
import com.evoapps.evoshift.viewmodels.JoinTeamViewModel
import com.evoapps.evoshift.viewmodels.VoiceActionViewModel
import com.evoapps.evoshift.viewmodels.ManagerDashboardViewModel
import com.evoapps.evoshift.viewmodels.OwnerDashboardViewModel
import com.evoapps.evoshift.viewmodels.ProfileViewModel
import com.evoapps.evoshift.viewmodels.RecurringCostRulesViewModel
import com.evoapps.evoshift.viewmodels.RootViewModel
import com.evoapps.evoshift.viewmodels.OnboardingViewModel
import com.evoapps.evoshift.viewmodels.RosterDetailViewModel
import com.evoapps.evoshift.viewmodels.RosterSkeletonViewModel
import com.evoapps.evoshift.viewmodels.ScheduleViewModel
import com.evoapps.evoshift.viewmodels.SkeletonEditorViewModel
import com.evoapps.evoshift.viewmodels.TeamManagementViewModel
import com.evoapps.evoshift.viewmodels.WeeklyTemplatesViewModel
import com.evoapps.evoshift.viewmodels.WorkplaceCreationViewModel
import com.evoapps.evoshift.viewmodels.WorkplaceSettingsViewModel
import com.evoapps.evoshift.viewmodels.PlanChoiceViewModel
import com.evoapps.evoshift.viewmodels.SubscriptionViewModel
import com.evoapps.evoshift.viewmodels.OverPlanViewModel
import com.evoapps.evoshift.viewmodels.NotificationSettingsViewModel
import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.auth.FirebaseAuth
import dev.gitlive.firebase.auth.auth
import org.kodein.di.DI
import org.kodein.di.DirectDIAware
import org.kodein.di.bind
import org.kodein.di.factory
import org.kodein.di.instance
import org.kodein.di.provider
import org.kodein.di.singleton

val appModule = DI {
    import(dataModule)
    import(domainModule)
    import(platformModule())

    bind<FirebaseAuth>() with singleton { Firebase.auth }

    // Crash reports and usage statistics (SH-86): the consent gate in front of the platform's
    // TelemetryBackend (platformModule). Off until the person allows them; never in debug builds.
    bind<ConsentStore>() with singleton { platformConsentStore() }
    bind<Telemetry>() with singleton {
        GatedTelemetry(instance(), canCollect = instance<TelemetryPlatform>().canCollect).also(AppTelemetry::install)
    }
    bind<PrivacyConsent>() with singleton { PrivacyConsent(instance(), instance(), instance()) }
    bind<PrivacyChoicesViewModel>() with provider { PrivacyChoicesViewModel(instance()) }
    // SH-135: sharing availability with every employer (Profile, and the one-time question).
    bind<com.evoapps.evoshift.viewmodels.AvailabilitySharingViewModel>() with provider {
        com.evoapps.evoshift.viewmodels.AvailabilitySharingViewModel(instance(), instance(), instance())
    }
    bind<com.evoapps.evoshift.viewmodels.MyStaffDetailsViewModel>() with provider {
        com.evoapps.evoshift.viewmodels.MyStaffDetailsViewModel(instance(), instance())
    }

    // Global session manager
    bind<WorkplaceSessionManager>() with singleton { WorkplaceSessionManager(platformLastWorkplaceStore()) }
    // SH-89: the workplace and month every screen shows (the top bar changes them).
    bind<WorkplaceMonthSelection>() with singleton { WorkplaceMonthSelection(instance()) }
    // One instance: the bell badge on every screen and the notifications screen share it.
    bind<NotificationsViewModel>() with singleton { newNotificationsViewModel() }
    // SH-164: the pending-request badges (Requests tab or More) and the owner's inbox card.
    bind<com.evoapps.evoshift.viewmodels.PendingRequestsViewModel>() with singleton { newPendingRequestsViewModel() }
    // SH-148: the app's saves, run to completion in the app's own scope (not a screen's).
    bind<com.evoapps.evoshift.saves.PendingSaves>() with singleton { com.evoapps.evoshift.saves.PendingSaves() }
    bind<ShiftWidgetSync>() with singleton {
        // SH-200: the widget follows the colours picked in the app.
        ShiftWidgetSync(
            instance(), instance(), instance(), instance(), platformShiftWidgetStore(), platformWatchStore(),
            appearance = instance<com.evoapps.evoshift.settings.ThemeSettings>().appearance
        )
    }
    // The device's push token and app language, saved at every signed-in start (SH-109) and
    // whenever the language is picked again (SH-137).
    bind<DeviceTokenSync>() with singleton { DeviceTokenSync(instance(), instance(), instance()) }
    // SH-240: the store rating prompt at a success moment (Android and iOS only).
    bind<com.evoapps.evoshift.review.ReviewPrompter>() with singleton {
        com.evoapps.evoshift.review.ReviewPrompter(
            settings = instance(), repository = instance(), authRepository = instance(),
            rosterRepository = instance(), financialRepository = instance(),
            launcher = com.evoapps.evoshift.review.platformStoreReviewLauncher(),
            scope = kotlinx.coroutines.CoroutineScope(kotlinx.coroutines.SupervisorJob() + kotlinx.coroutines.Dispatchers.Main)
        )
    }
    // SH-137/SH-138: the language picked in the app (kept on the device and on the profile).
    bind<com.evoapps.evoshift.settings.LocalSettings>() with singleton { com.evoapps.evoshift.settings.platformLocalSettings() }
    // SH-168: the device's connection, for the shell's offline banner.
    bind<com.evoapps.evoshift.connectivity.ConnectivityWatcher>() with singleton { com.evoapps.evoshift.connectivity.platformConnectivityWatcher() }
    bind<com.evoapps.evoshift.settings.AppLanguageSettings>() with singleton { com.evoapps.evoshift.settings.AppLanguageSettings(instance()) }
    bind<com.evoapps.evoshift.settings.ProfileLanguageSync>() with singleton {
        com.evoapps.evoshift.settings.ProfileLanguageSync(instance(), instance(), instance())
    }
    bind<com.evoapps.evoshift.viewmodels.LanguageViewModel>() with provider { com.evoapps.evoshift.viewmodels.LanguageViewModel(instance()) }
    // SH-196: the app's colours (kept on the device and on the profile).
    bind<com.evoapps.evoshift.settings.ThemeSettings>() with singleton { com.evoapps.evoshift.settings.ThemeSettings(instance()) }
    bind<com.evoapps.evoshift.settings.ProfileAppearanceSync>() with singleton {
        com.evoapps.evoshift.settings.ProfileAppearanceSync(instance(), instance(), instance())
    }
    bind<com.evoapps.evoshift.viewmodels.AppearanceViewModel>() with provider {
        com.evoapps.evoshift.viewmodels.AppearanceViewModel(instance(), instance(), instance())
    }
    // SH-146: "What's new" once after an update; an install that remembers a workplace was used before.
    bind<com.evoapps.evoshift.viewmodels.WhatsNewHistory>() with singleton {
        com.evoapps.evoshift.viewmodels.WhatsNewHistory(instance(), com.evoapps.evoshift.ui.whatsnew.WhatsNew.RELEASE) {
            platformLastWorkplaceStore().load() != null
        }
    }
    bind<com.evoapps.evoshift.viewmodels.WhatsNewViewModel>() with provider { com.evoapps.evoshift.viewmodels.WhatsNewViewModel(instance()) }
    // The watch's clock in / out, done on the phone (SH-99, androidApp WatchBridgeService).
    bind<WatchClockHandler>() with singleton { WatchClockHandler(instance(), instance(), instance(), plans = instance()) }

    bind<AuthViewModel>() with provider {
        AuthViewModel(
            loginUseCase = instance(),
            registerUserUseCase = instance(),
            googleSignInUseCase = instance(),
            appleSignInUseCase = instance(),
            resetPasswordUseCase = instance(),
            registerDeviceTokenUseCase = instance(),
            sendEmailVerificationUseCase = instance(),
            checkEmailVerificationUseCase = instance(),
            getCurrentUserUseCase = instance(),
            deviceInfoProvider = instance()
        )
    }

    bind<RootViewModel>() with provider {
        RootViewModel(
            getCurrentUserUseCase = instance(),
            checkEmailVerificationUseCase = instance(),
            kioskSessions = instance<com.evoapps.evoshift.domain.repository.KioskRepository>().observeSession()
        )
    }

    // --- SH-107: the clock-in tablet ---
    bind<com.evoapps.evoshift.domain.usecase.kiosk.KioskStore>(tag = com.evoapps.evoshift.data.di.KIOSK_SESSION_STORE) with singleton {
        com.evoapps.evoshift.kiosk.platformKioskStore("session")
    }
    bind<com.evoapps.evoshift.domain.usecase.kiosk.KioskClockQueue>() with singleton {
        com.evoapps.evoshift.domain.usecase.kiosk.KioskClockQueue(com.evoapps.evoshift.kiosk.platformKioskStore(com.evoapps.evoshift.kiosk.KioskStores.QUEUE))
    }
    bind<com.evoapps.evoshift.domain.usecase.kiosk.KioskClockUseCase>() with singleton {
        com.evoapps.evoshift.domain.usecase.kiosk.KioskClockUseCase(instance(), instance())
    }
    bind<com.evoapps.evoshift.kiosk.KioskDeviceLock>() with singleton { com.evoapps.evoshift.kiosk.platformKioskDeviceLock() }
    bind<com.evoapps.evoshift.viewmodels.KioskViewModel>() with factory { session: com.evoapps.evoshift.domain.model.KioskSession ->
        com.evoapps.evoshift.viewmodels.KioskViewModel(
            session, instance(), instance(), instance(), instance(), checklists = instance(),
            // SH-134: the optional clock-in photo.
            photos = instance(), photoProcessor = instance()
        )
    }
    // SH-124: the day's tips on the tablet (a manager's PIN).
    bind<com.evoapps.evoshift.viewmodels.KioskTipsViewModel>() with factory { session: com.evoapps.evoshift.domain.model.KioskSession ->
        com.evoapps.evoshift.viewmodels.KioskTipsViewModel(session, instance())
    }
    bind<com.evoapps.evoshift.viewmodels.KioskPairViewModel>() with provider { com.evoapps.evoshift.viewmodels.KioskPairViewModel(instance()) }
    bind<com.evoapps.evoshift.viewmodels.KioskDevicesViewModel>() with factory { key: com.evoapps.evoshift.ui.kiosk.KioskDevicesKey ->
        com.evoapps.evoshift.viewmodels.KioskDevicesViewModel(key.workplaceId, instance())
    }
    bind<com.evoapps.evoshift.viewmodels.KioskPinViewModel>() with provider { com.evoapps.evoshift.viewmodels.KioskPinViewModel(instance()) }
    // SH-111: the phone scans the tablet's rotating QR.
    bind<com.evoapps.evoshift.kiosk.QrScanner>() with singleton { com.evoapps.evoshift.kiosk.platformQrScanner() }
    bind<com.evoapps.evoshift.viewmodels.QrClockViewModel>() with provider { com.evoapps.evoshift.viewmodels.QrClockViewModel(instance(), instance()) }

    bind<ProfileViewModel>() with provider {
        ProfileViewModel(
            getCurrentUserUseCase = instance(),
            updateUserProfileUseCase = instance(),
            signOutUseCase = instance(),
            deleteAccountUseCase = instance(),
            getOwnerUseCase = instance(),
            updateOwnerBusinessNameUseCase = instance(),
            getRecurringCostRulesUseCase = instance(),
            getWorkplaceUseCase = instance(),
            getSharedCostsUseCase = instance(),
            sharedCostChanges = instance(),
            saves = instance(),
            connectivity = instance()
        )
    }

    // SH-94: the profile photo: decoding/cropping on the device, avatars from memory.
    bind<com.evoapps.evoshift.photo.ProfilePhotoProcessor>() with singleton { com.evoapps.evoshift.photo.PlatformProfilePhotoProcessor() }
    bind<com.evoapps.evoshift.photo.AvatarImages>() with singleton { com.evoapps.evoshift.photo.AvatarImages(instance()) }
    bind<com.evoapps.evoshift.viewmodels.ProfilePhotoViewModel>() with provider {
        com.evoapps.evoshift.viewmodels.ProfilePhotoViewModel(
            getCurrentUserUseCase = instance(),
            uploadProfilePhoto = instance(),
            removeProfilePhoto = instance(),
            processor = instance(),
            avatarImages = instance()
        )
    }

    // SH-118: team announcements; the note on a shift (roster builder's shift dialog).
    bind<com.evoapps.evoshift.viewmodels.AnnouncementsViewModel>() with provider {
        com.evoapps.evoshift.viewmodels.AnnouncementsViewModel(
            getCurrentUserUseCase = instance(),
            sessionManager = instance(),
            repository = instance(),
            postAnnouncement = instance(),
            markRead = instance(),
            getWorkplace = instance(),
            getTeam = instance(),
            userRepository = instance(),
            observePlanActive = instance()
        )
    }
    // SH-132: the workplace location dialog (address suggestions, map pin), one per opening.
    bind<com.evoapps.evoshift.viewmodels.AddressSearchTiming>() with singleton { com.evoapps.evoshift.viewmodels.AddressSearchTiming() }
    bind<com.evoapps.evoshift.viewmodels.WorkplaceLocationViewModel>() with factory { key: com.evoapps.evoshift.viewmodels.WorkplaceLocationKey ->
        com.evoapps.evoshift.viewmodels.WorkplaceLocationViewModel(
            key, search = instance(), timing = instance(), language = { com.evoapps.evoshift.util.appLanguage() }
        )
    }
    // OpenStreetMap tiles for the map (one cache for the whole session).
    bind<com.evoapps.evoshift.ui.map.MapTileSource>() with singleton { com.evoapps.evoshift.ui.map.OsmTileSource(instance()) }
    bind<com.evoapps.evoshift.viewmodels.ShiftNoteViewModel>() with factory { key: com.evoapps.evoshift.viewmodels.ShiftNoteKey ->
        com.evoapps.evoshift.viewmodels.ShiftNoteViewModel(key, repository = instance(), saveShiftNote = instance(), getCurrentUserUseCase = instance())
    }
    // SH-122: a person's documents, the workplace's policies.
    bind<com.evoapps.evoshift.viewmodels.StaffDocumentsViewModel>() with factory { args: Route.StaffDocuments ->
        com.evoapps.evoshift.viewmodels.StaffDocumentsViewModel(
            workplaceId = args.workplaceId,
            userId = args.userId,
            getCurrentUser = instance(),
            getDocuments = instance(),
            uploadDocument = instance(),
            deleteDocument = instance(),
            openDocument = instance(),
            opener = instance(),
            users = instance(),
            observePlanActive = instance()
        )
    }
    // SH-270: roles (the owner) and one person's access at a workplace.
    bind<com.evoapps.evoshift.viewmodels.RolesViewModel>() with provider {
        com.evoapps.evoshift.viewmodels.RolesViewModel(
            getCurrentUser = instance(),
            getRoles = instance(),
            saveRole = instance(),
            deleteRole = instance()
        )
    }
    bind<com.evoapps.evoshift.viewmodels.PersonAccessViewModel>() with factory { args: Route.PersonAccess ->
        com.evoapps.evoshift.viewmodels.PersonAccessViewModel(
            userId = args.userId,
            workplaceId = args.workplaceId,
            getCurrentUser = instance(),
            getWorkplace = instance(),
            getEmployee = instance(),
            getRoles = instance(),
            setAccess = instance()
        )
    }
    // SH-212: a person's timeline (effective-dated hire, contract, pay, statuses, OHS, notes).
    bind<com.evoapps.evoshift.viewmodels.EmployeeTimelineViewModel>() with factory { args: Route.EmployeeTimeline ->
        com.evoapps.evoshift.viewmodels.EmployeeTimelineViewModel(
            workplaceId = args.workplaceId,
            userId = args.userId,
            getCurrentUser = instance(),
            timelines = instance(),
            observePlanActive = instance(),
            payTermsChanges = instance(),
            saves = instance(),
            requests = instance()
        )
    }
    // SH-126: training courses and the OHS training record.
    bind<com.evoapps.evoshift.viewmodels.CoursesViewModel>() with factory { args: Route.Courses ->
        com.evoapps.evoshift.viewmodels.CoursesViewModel(
            workplaceId = args.workplaceId,
            getCurrentUser = instance(),
            getMyCourses = instance(),
            getManagedCourses = instance(),
            getOhsRecord = instance(),
            getTrainingStatuses = instance(),
            users = instance(),
            observePlanActive = instance()
        )
    }
    bind<com.evoapps.evoshift.viewmodels.CourseEditorViewModel>() with factory { args: Route.CourseEditor ->
        com.evoapps.evoshift.viewmodels.CourseEditorViewModel(
            workplaceId = args.workplaceId,
            courseId = args.courseId,
            getCurrentUser = instance(),
            getCourse = instance(),
            saveCourse = instance(),
            deleteCourse = instance(),
            uploadImage = instance(),
            newCourseId = instance(),
            getWorkplace = instance(),
            uploadPresentation = instance(),
            observePlanActive = instance()
        )
    }
    bind<com.evoapps.evoshift.viewmodels.TakeCourseViewModel>() with factory { args: Route.TakeCourse ->
        com.evoapps.evoshift.viewmodels.TakeCourseViewModel(
            workplaceId = args.workplaceId,
            courseId = args.courseId,
            getCurrentUser = instance(),
            getCourse = instance(),
            saveProgress = instance(),
            submitCourse = instance(),
            loadImage = instance(),
            loadPresentation = instance(),
            opener = instance()
        )
    }
    bind<com.evoapps.evoshift.viewmodels.CourseProgressViewModel>() with factory { args: Route.CourseProgress ->
        com.evoapps.evoshift.viewmodels.CourseProgressViewModel(
            workplaceId = args.workplaceId,
            courseId = args.courseId,
            getCurrentUser = instance(),
            getCourse = instance(),
            getReport = instance(),
            assignCourse = instance(),
            users = instance(),
            observePlanActive = instance()
        )
    }
    bind<com.evoapps.evoshift.viewmodels.OhsRecordViewModel>() with factory { args: Route.OhsTraining ->
        com.evoapps.evoshift.viewmodels.OhsRecordViewModel(
            workplaceId = args.workplaceId,
            userId = args.userId,
            getCurrentUser = instance(),
            getOhsRecord = instance(),
            recordOhs = instance(),
            confirmOhs = instance(),
            getDocument = instance(),
            users = instance(),
            observePlanActive = instance()
        )
    }
    bind<com.evoapps.evoshift.viewmodels.MyTrainingViewModel>() with provider {
        com.evoapps.evoshift.viewmodels.MyTrainingViewModel(instance(), instance(), instance(), instance())
    }
    bind<com.evoapps.evoshift.viewmodels.PoliciesViewModel>() with factory { args: Route.Policies ->
        com.evoapps.evoshift.viewmodels.PoliciesViewModel(
            workplaceId = args.workplaceId,
            getCurrentUser = instance(),
            getPolicies = instance(),
            publishPolicy = instance(),
            deletePolicy = instance(),
            openPolicy = instance(),
            acknowledgePolicy = instance(),
            getReadReport = instance(),
            opener = instance(),
            observePlanActive = instance()
        )
    }

    bind<NotificationSettingsViewModel>() with provider {
        NotificationSettingsViewModel(getCurrentUserUseCase = instance(), repository = instance())
    }

    bind<RecurringCostRulesViewModel>() with provider {
        RecurringCostRulesViewModel(
            getCurrentUserUseCase = instance(),
            getWorkplaceUseCase = instance(),
            getRecurringCostRulesUseCase = instance(),
            createRecurringCostRuleUseCase = instance(),
            updateRecurringCostRuleUseCase = instance(),
            deleteRecurringCostRuleUseCase = instance(),
            migrateOneOffCostRulesUseCase = instance()
        )
    }

    // SH-267: the owner's workplace groups.
    bind<com.evoapps.evoshift.viewmodels.WorkplaceGroupsViewModel>() with provider {
        com.evoapps.evoshift.viewmodels.WorkplaceGroupsViewModel(
            getCurrentUserUseCase = instance(),
            getWorkplaceUseCase = instance(),
            getGroups = instance(),
            saveGroups = instance(),
            // SH-268: the group managers.
            saveManagers = instance(),
            getEmployees = instance()
        )
    }

    // SH-131: the owner's shared costs; the route may name a rule to convert.
    bind<com.evoapps.evoshift.viewmodels.SharedCostsViewModel>() with factory { args: Route.SharedCosts ->
        com.evoapps.evoshift.viewmodels.SharedCostsViewModel(
            getCurrentUserUseCase = instance(),
            getWorkplaceUseCase = instance(),
            getSharedCosts = instance(),
            saveSharedCost = instance(),
            deleteSharedCost = instance(),
            getBusinessMonth = instance(),
            getRecurringCostRules = instance(),
            convertRuleId = args.convertRuleId
        )
    }

    bind<WorkplaceCreationViewModel>() with provider {
        WorkplaceCreationViewModel(
            createWorkplaceUseCase = instance(),
            getCurrentUserUseCase = instance(),
            getOwnerUseCase = instance(),
            checkPlanLimit = instance()
        )
    }

    // The owner's plan (SH-38)
    bind<PlanChoiceViewModel>() with provider {
        PlanChoiceViewModel(
            getPlanOffers = instance(),
            purchasePlan = instance(),
            restorePurchases = instance(),
            observeOwnerPlan = instance(),
            observeSeatsInUse = instance()
        )
    }
    // SH-220: the owner's over-plan banner
    bind<OverPlanViewModel>() with provider {
        OverPlanViewModel(
            observeOwnerPlan = instance(),
            observeSeatsInUse = instance(),
            getPlanOffers = instance(),
            purchasePlan = instance()
        )
    }
    bind<SubscriptionViewModel>() with provider {
        SubscriptionViewModel(
            observeOwnerPlan = instance(),
            manageSubscription = instance(),
            restorePurchases = instance(),
            observeSeatsInUse = instance()
        )
    }

    bind<OnboardingViewModel>() with provider {
        OnboardingViewModel(
            getCurrentUser = instance(),
            getOwner = instance(),
            createWorkplace = instance(),
            updateWorkplaceName = instance(),
            updateBusinessName = instance(),
            createStarterSchedule = instance(),
            validateInvite = instance(),
            acceptInvite = instance(),
            completeSetup = instance(),
            signOutUseCase = instance(),
            getWorkplace = instance(),
            updateWorkplaceSettings = instance(),
            updateShiftTemplate = instance(),
            saveSalesTargets = instance(),
            checkPlanLimit = instance(),
            saves = instance(),
            connectivity = instance()
        )
    }

    bind<TeamManagementViewModel>() with provider {
        TeamManagementViewModel(
            getTeamStatusUseCase = instance(),
            sendInviteUseCase = instance(),
            terminateUseCase = instance(),
            getCurrentUserUseCase = instance(),
            getRosterSummaryUseCase = instance(),
            sessionManager = instance(),
            observePlanActive = instance(),
            getTeamReadiness = instance(),
            getWorkplace = instance(),
            // SH-267: "Guests this month".
            borrowing = instance(),
            userRepository = instance()
        )
    }

    // SH-269: the Team screen's selection and "Edit selected".
    bind<com.evoapps.evoshift.viewmodels.BulkEmployeeEditViewModel>() with provider {
        com.evoapps.evoshift.viewmodels.BulkEmployeeEditViewModel(
            sessionManager = instance(),
            getCurrentUser = instance(),
            getEmployees = instance(),
            getWorkplace = instance(),
            users = instance(),
            bulk = instance(),
            getGroups = instance()
        )
    }

    // Shared dashboard VM
    bind<DashboardViewModel>() with singleton { newDashboardViewModel() }

    bind<LocationNoticeStore>() with singleton { platformLocationNoticeStore() }
    bind<MyShiftClock>() with provider { MyShiftClock(instance(), instance(), plans = instance(), locationNotice = instance()) }

    bind<EmployeeDashboardViewModel>() with provider {
        EmployeeDashboardViewModel(
            getCurrentUserUseCase = instance(),
            getRosterSummaryUseCase = instance(),
            getEmployeeDashboardUseCase = instance(),
            sessionManager = instance(),
            myShiftClock = instance(),
            workplaceRepository = instance(),
            monthSelection = instance()
        )
    }

    bind<ManagerDashboardViewModel>() with provider {
        ManagerDashboardViewModel(
            getCurrentUserUseCase = instance(),
            getRosterSummaryUseCase = instance(),
            getManagerDashboardUseCase = instance(),
            sessionManager = instance(),
            myShiftClock = instance(),
            getRosterShiftsUseCase = instance(),
            observePlanActive = instance(),
            getTeamReadiness = instance(),
            monthSelection = instance(),
            workplaceRepository = instance(),
            observeRosterChanges = instance(),
            observeWeather = instance()
        )
    }

    bind<OwnerDashboardViewModel>() with provider {
        OwnerDashboardViewModel(
            getCurrentUserUseCase = instance(),
            getRosterSummaryUseCase = instance(),
            getManagerDashboardUseCase = instance(),
            sessionManager = instance(),
            getMonthTrendUseCase = instance(),
            workplaceRepository = instance(),
            findMissingDailyResultsUseCase = instance(),
            submitDailyRevenueUseCase = instance(),
            observePlanActive = instance(),
            getTeamReadiness = instance(),
            monthSelection = instance(),
            getStaffingHints = instance(),
            getBusinessMonth = instance(),
            getAssignableEmployees = instance(),
            observeRosterChanges = instance(),
            observeWeather = instance(),
            getSalesComparison = instance()
        )
    }

    bind<JoinTeamViewModel>() with provider {
        JoinTeamViewModel(
            validateInviteUseCase = instance(),
            acceptInviteUseCase = instance(),
            getCurrentUserUseCase = instance(),
            recommendPrompt = instance()
        )
    }

    // Per screen, not a singleton: it holds financial data, which must not outlive the signed-in
    // user (an employee signing in on the same device must not see the previous manager's figures).
    bind<FinancialsViewModel>() with provider {
        FinancialsViewModel(
            getCurrentUserUseCase = instance(),
            getRosterSummaryUseCase = instance(),
            financialRepository = instance(),
            sessionManager = instance(),
            workplaceRepository = instance(),
            addOneOffCostUseCase = instance(),
            deleteOneOffCostUseCase = instance(),
            observePlanActive = instance(),
            monthSelection = instance(),
            getLabourBudget = instance(),
            sharedCostChanges = instance(),
            payTermsChanges = instance(),
            getMonthTrend = instance(),
            getAssignableEmployees = instance(),
            getSalesComparison = instance()
        )
    }

    // SH-150: the voice assistant's screen.
    bind<VoiceActionViewModel>() with factory { args: Route.VoiceAction ->
        VoiceActionViewModel(
            action = com.evoapps.evoshift.voice.VoiceAction.fromPath(args.action) ?: com.evoapps.evoshift.voice.VoiceAction.NEXT_SHIFT,
            getCurrentUserUseCase = instance(),
            shiftRepository = instance(),
            myShiftClock = instance()
        )
    }

    bind<CreateRosterViewModel>() with factory { args: Route.CreateRoster ->
        CreateRosterViewModel(
            workplaceId = args.workplaceId,
            initialWorkplaceName = args.workplaceName,
            createRosterUseCase = instance(),
            getCurrentUserUseCase = instance(),
            applyWeeklySkeleton = instance(),
            initialYear = args.year,
            initialMonth = args.month
        )
    }

    // GDPR data exports (SH-27): one per target (my data, a workplace's).
    bind<com.evoapps.evoshift.viewmodels.DataExportViewModel>() with factory { target: com.evoapps.evoshift.viewmodels.ExportTarget ->
        com.evoapps.evoshift.viewmodels.DataExportViewModel(
            target = target,
            exportMyData = instance(),
            exportWorkplace = instance(),
            signOut = instance()
        )
    }
    bind<WorkplaceSettingsViewModel>() with factory { args: Route.WorkplaceSettings ->
        WorkplaceSettingsViewModel(
            initialWorkplaceId = args.workplaceId,
            updateSettingsUseCase = instance(),
            createCostRuleUseCase = instance(),
            getWorkplaceUseCase = instance(),
            updateWorkplaceNameUseCase = instance(),
            updateWorkplaceLocationUseCase = instance(),
            updateWorkplaceFinancialsUseCase = instance(),
            deleteWorkplaceUseCase = instance(),
            workplaceRepository = instance(),
            sessionManager = instance(),
            getCurrentUserUseCase = instance(),
            observePlanActive = instance(),
            getLabourBudget = instance(),
            saveLabourBudget = instance(),
            saves = instance(),
            connectivity = instance(),
            // SH-223: the shared places to work from.
            workPlaces = instance(),
            // The owner's contract types for the request types' settings.
            ownerRepository = instance(),
            // SH-239: hiding the EVoShift footer needs a paid plan.
            observeWorkplacePlan = instance(),
            // SH-269: group settings: where each section's values come from, "Reset to group".
            groupSettings = instance(),
            setSectionInheritance = instance()
        )
    }

    // SH-269: the same screen on a group's values (the owner).
    bind<WorkplaceSettingsViewModel>() with factory { args: Route.GroupSettings ->
        WorkplaceSettingsViewModel(
            initialWorkplaceId = "",
            updateSettingsUseCase = instance(),
            createCostRuleUseCase = instance(),
            getWorkplaceUseCase = instance(),
            updateWorkplaceNameUseCase = instance(),
            updateWorkplaceLocationUseCase = instance(),
            updateWorkplaceFinancialsUseCase = instance(),
            deleteWorkplaceUseCase = instance(),
            workplaceRepository = instance(),
            sessionManager = instance(),
            getCurrentUserUseCase = instance(),
            observePlanActive = instance(),
            saves = instance(),
            connectivity = instance(),
            ownerRepository = instance(),
            observeWorkplacePlan = instance(),
            groupSettings = instance(),
            saveGroupSettings = instance(),
            groupId = args.groupId,
            getGroups = instance()
        )
    }

    bind<SkeletonEditorViewModel>() with factory { workplaceId: String ->
        SkeletonEditorViewModel(
            workplaceId = workplaceId,
            getSkeletonEditorData = instance(),
            updateWeeklySkeleton = instance(),
            createDayTemplate = instance(),
            addShiftToDayTemplate = instance(),
            removeShiftFromDayTemplate = instance(),
            createShiftTemplate = instance(),
            renameDayTemplate = instance(),
            deleteDayTemplate = instance(),
            duplicateDayTemplate = instance(),
            updateShiftTemplate = instance(),
            deleteShiftTemplate = instance(),
            getWorkplaceUseCase = instance()
        )
    }

    bind<RosterDetailViewModel>() with factory { rosterId: String ->
        RosterDetailViewModel(
            rosterId = rosterId,
            getRosterUseCase = instance(),
            getRosterShiftsUseCase = instance(),
            publishRosterUseCase = instance(),
            getAssignableEmployeesUseCase = instance(),
            getRosterAvailabilityUseCase = instance(),
            assignUserToShiftUseCase = instance(),
            unassignShiftFromEmployeeUseCase = instance(),
            createShiftUseCase = instance(),
            updateShiftUseCase = instance(),
            workplaceRepository = instance(),
            getCurrentUserUseCase = instance(),
            detectRosterConflictsUseCase = instance(),
            markShiftsOpenUseCase = instance(),
            userRepository = instance(),
            observePlanActive = instance(),
            getTeamReadiness = instance(),
            getDayClosures = instance(),
            planAutoSchedule = instance(),
            applyAutoSchedule = instance(),
            suggestWithAi = instance(),
            getStaffingHints = instance(),
            copyShifts = instance(),
            getBusyElsewhere = instance(),
            getLabourBudget = instance(),
            deleteShiftUseCase = instance(),
            calculateShiftCost = instance(),
            regenerateDailyRecords = instance(),
            saves = instance(),
            absenceRepository = instance(),
            localSettings = instance(),
            // SH-210: interviews in the grid.
            interviewRepository = instance(),
            observeWeather = instance(),
            // SH-227: events and special days in the day headers, the event dialog.
            observeSpecialDays = instance(),
            saveLocalEvent = instance(),
            deleteLocalEvent = instance(),
            // SH-223: remote work and work events marked in the grid.
            workRequestRepository = instance(),
            // SH-236: who actually worked, splitting shifts.
            recordActualWork = instance(),
            splitShiftUseCase = instance(),
            undoShiftSplit = instance(),
            // SH-267: people to borrow from the other workplaces of the group.
            borrowing = instance()
        )
    }

    bind<EmployeeFormViewModel>() with factory { args: Route.EmployeeForm ->
        EmployeeFormViewModel(
            workplaceId = args.workplaceId,
            employeeId = args.employeeId,
            createEmployeeUseCase = instance(),
            updateEmployeeUseCase = instance(),
            getEmployeeDetailsUseCase = instance(),
            getWorkplaceUseCase = instance(),
            sendInviteUseCase = instance(),
            getCurrentUserUseCase = instance(),
            terminateEngagementUseCase = instance(),
            repriceFutureShifts = instance<com.evoapps.evoshift.domain.usecase.finance.RepriceFutureShiftsUseCase>().let { reprice ->
                { workplaceId, from -> reprice(workplaceId, from) }
            },
            checkSeats = instance(),
            observePlanActive = instance(),
            getStaffDetails = instance(),
            saveStaffRecord = instance(),
            saveAvailabilityNotes = instance(),
            // SH-125: "Hire" from an applicant.
            applicant = args.applicantPostId?.let { post -> args.applicantId?.let { post to it } },
            hiringRepository = instance(),
            markApplicantHired = instance(),
            setWorksUnpaid = instance(),
            payTermsChanges = instance(),
            // SH-206: "Count in labour cost and SPMH".
            getLabourInclusion = instance(),
            setLabourInclusion = instance(),
            saves = instance(),
            connectivity = instance(),
            // SH-192: "From your other workplaces".
            getPeopleFromOtherWorkplaces = instance(),
            addEngagement = instance(),
            // SH-250: the pay shown follows the stored terms (a timeline edit shows at once).
            observePayTerms = instance<com.evoapps.evoshift.domain.repository.UserRepository>().let { users ->
                { userId, workplaceId -> users.observeFinancialEngagement(userId, workplaceId) }
            },
            // SH-255: "Hide in the roster for others".
            getHiddenInRoster = instance(),
            setHiddenInRoster = instance()
        )
    }

    // SH-217: importing a month's roster from a spreadsheet (written through the copy tool).
    bind<com.evoapps.evoshift.domain.rosterimport.GetRosterImportContextUseCase>() with provider {
        com.evoapps.evoshift.domain.rosterimport.GetRosterImportContextUseCase(
            rosters = instance(), workplaces = instance(), users = instance(), shifts = instance(), codes = instance()
        )
    }
    bind<com.evoapps.evoshift.domain.rosterimport.SaveRosterCodesUseCase>() with provider {
        com.evoapps.evoshift.domain.rosterimport.SaveRosterCodesUseCase(codes = instance())
    }
    bind<com.evoapps.evoshift.viewmodels.RosterImportViewModel>() with factory { args: Route.RosterImport ->
        com.evoapps.evoshift.viewmodels.RosterImportViewModel(
            rosterId = args.rosterId,
            getCurrentUser = instance(),
            getContext = instance(),
            saveCodes = instance(),
            importEmployee = instance(),
            copyShifts = instance(),
            checkSeats = instance(),
            observePlanActive = instance(),
            saves = instance()
        )
    }

    // SH-214: importing the team from a spreadsheet.
    bind<com.evoapps.evoshift.domain.staffimport.ImportEmployeeUseCase>() with provider {
        com.evoapps.evoshift.domain.staffimport.ImportEmployeeUseCase(createEmployee = instance(), sendInvite = instance())
    }
    bind<com.evoapps.evoshift.domain.staffimport.GetTeamForImportUseCase>() with provider {
        com.evoapps.evoshift.domain.staffimport.GetTeamForImportUseCase(users = instance())
    }
    bind<com.evoapps.evoshift.viewmodels.StaffImportViewModel>() with factory { args: Route.StaffImport ->
        com.evoapps.evoshift.viewmodels.StaffImportViewModel(
            workplaceId = args.workplaceId,
            getCurrentUser = instance(),
            getWorkplace = instance(),
            getTeam = instance(),
            importEmployee = instance(),
            fileSaver = instance(),
            checkSeats = instance(),
            observePlanActive = instance(),
            connectivity = instance(),
            language = { com.evoapps.evoshift.util.appLanguage() }
        )
    }

    // SH-125: hiring - the posts, a post's editor, its applicants; the share sheet.
    bind<com.evoapps.evoshift.util.TextSharer>() with singleton { com.evoapps.evoshift.util.platformTextSharer() }
    bind<com.evoapps.evoshift.viewmodels.HiringViewModel>() with provider {
        com.evoapps.evoshift.viewmodels.HiringViewModel(
            repository = instance(), getCurrentUser = instance(), sessionManager = instance(), observePlanActive = instance()
        )
    }
    bind<com.evoapps.evoshift.viewmodels.RecruitingViewModel>() with provider {
        com.evoapps.evoshift.viewmodels.RecruitingViewModel(repository = instance(), getCurrentUser = instance(), sessionManager = instance())
    }
    bind<com.evoapps.evoshift.viewmodels.JobPostEditorViewModel>() with factory { args: Route.JobPostEditor ->
        com.evoapps.evoshift.viewmodels.JobPostEditorViewModel(
            postId = args.postId, repository = instance(), savePost = instance(), getCurrentUser = instance(),
            sessionManager = instance(), getWorkplace = instance(), observePlanActive = instance(),
            getTeam = instance()
        )
    }
    bind<com.evoapps.evoshift.viewmodels.ApplicantsViewModel>() with factory { args: Route.Applicants ->
        com.evoapps.evoshift.viewmodels.ApplicantsViewModel(
            postId = args.postId, repository = instance(), setStatus = instance(), getCurrentUser = instance(),
            observePlanActive = instance(),
            // SH-210: rejecting with an e-mail, interviews, the team.
            interviewRepository = instance(), rejectApplicant = instance(), scheduleInterview = instance(),
            cancelInterview = instance(), getTeam = instance()
        )
    }

    bind<RosterSkeletonViewModel>() with factory { rosterId: String ->
        RosterSkeletonViewModel(
            rosterId = rosterId,
            getRosterUseCase = instance(),
            workplaceRepository = instance(),
            shiftRepository = instance(),
            finalizeRosterSkeletonUseCase = instance(),
            deleteShiftUseCase = instance(),
            getCurrentUserUseCase = instance(),
            getRevenuePlanUseCase = instance(),
            sendAvailabilityRequestUseCase = instance(),
            observePlanActive = instance(),
            applyWeeklySkeleton = instance(),
            getMonthAnswers = instance(),
            saves = instance(),
            connectivity = instance()
        )
    }

    bind<AvailabilityViewModel>() with provider {
        AvailabilityViewModel(
            getRosterSummaryUseCase = instance(),
            getAvailabilityRequestUseCase = instance(),
            getCurrentUserUseCase = instance(),
            getWeeklyAvailabilityTemplatesUseCase = instance(),
            sessionManager = instance()
        )
    }

    bind<AvailabilityFormViewModel>() with factory { rosterId: String ->
        AvailabilityFormViewModel(
            rosterId = rosterId,
            getRosterUseCase = instance(),
            getMyAvailabilityUseCase = instance(),
            getCurrentUserUseCase = instance(),
            getWorkplaceUseCase = instance(),
            submitAvailabilityUseCase = instance(),
            getWeeklyAvailabilityTemplatesUseCase = instance(),
            saveWeeklyAvailabilityTemplateUseCase = instance(),
            applyWeeklyAvailabilityTemplateUseCase = instance(),
            observePlanActive = instance(),
            getAvailabilityRequestUseCase = instance(),
            saves = instance(),
            connectivity = instance()
        )
    }

    bind<ScheduleViewModel>() with provider {
        ScheduleViewModel(
            getCurrentUserUseCase = instance(),
            getRosterSummaryUseCase = instance(),
            getRosterShiftsUseCase = instance(),
            getWorkplaceUseCase = instance(),
            requestShiftSwapUseCase = instance(),
            takeShiftFromPoolUseCase = instance(),
            updateShiftUseCase = instance(),
            sessionManager = instance(),
            userRepository = instance(),
            shiftRepository = instance(),
            observePlanActive = instance(),
            monthSelection = instance(),
            shiftNotes = instance(),
            cancelSwapRequest = instance(),
            getSwapCandidates = instance(),
            getOwnPayFacts = instance(),
            // SH-210: the person's job interviews.
            interviewRepository = instance(),
            // SH-223: the remote-work mark on the person's shifts.
            workRequestRepository = instance(),
            // SH-257: "Wpisz godziny pracy".
            reportWorkHours = instance()
        )
    }

    bind<ReportPrintViewModel>() with provider { ReportPrintViewModel(instance(), instance(), instance(), instance(), instance()) }

    // SH-45: "Email the roster" (roster builder) and "Add to calendar" (My shifts).
    bind<com.evoapps.evoshift.viewmodels.RosterMailViewModel>() with provider {
        com.evoapps.evoshift.viewmodels.RosterMailViewModel(instance(), instance(), observePlanActive = instance())
    }
    // SH-261: "Print roster" (roster builder).
    bind<com.evoapps.evoshift.viewmodels.RosterPrintViewModel>() with provider {
        com.evoapps.evoshift.viewmodels.RosterPrintViewModel(instance(), instance(), observePlanActive = instance())
    }
    bind<com.evoapps.evoshift.viewmodels.CalendarViewModel>() with provider {
        com.evoapps.evoshift.viewmodels.CalendarViewModel(
            instance(), instance(), instance(),
            getCurrentUser = instance(),
            localSettings = instance(),
            device = com.evoapps.evoshift.ui.calendar.platformCalendarDevice()
        )
    }

    bind<PayrollViewModel>() with factory { rosterId: String ->
        PayrollViewModel(
            rosterId = rosterId,
            generatePayrollReport = instance(),
            getCurrentUser = instance(),
            fileSaver = instance(),
            saveAdjustment = instance(),
            deleteAdjustment = instance(),
            observePlanActive = instance(),
            payTermsChanges = instance()
        )
    }
    // SH-147: the missing-pay warning of a month or a day (one per scope on a screen).
    bind<com.evoapps.evoshift.viewmodels.MissingPayViewModel>() with factory { scope: com.evoapps.evoshift.viewmodels.MissingPayScope ->
        com.evoapps.evoshift.viewmodels.MissingPayViewModel(
            scope = scope,
            getMissingPay = instance(),
            getCurrentUser = instance(),
            setWorksUnpaid = instance(),
            observePlanActive = instance(),
            payTermsChanges = instance()
        )
    }

    // SH-121: checklists.
    bind<com.evoapps.evoshift.viewmodels.ChecklistsViewModel>() with factory { args: Route.Checklists ->
        com.evoapps.evoshift.viewmodels.ChecklistsViewModel(
            args = args,
            getCurrentUserUseCase = instance(),
            sessionManager = instance(),
            observeTemplates = instance(),
            observeDay = instance(),
            saveTemplate = instance(),
            deleteTemplate = instance(),
            workplaceRepository = instance(),
            userRepository = instance(),
            plans = instance()
        )
    }
    bind<com.evoapps.evoshift.viewmodels.ShiftChecklistViewModel>() with factory { shiftId: String ->
        com.evoapps.evoshift.viewmodels.ShiftChecklistViewModel(
            shiftId = shiftId,
            getCurrentUserUseCase = instance(),
            shiftRepository = instance(),
            observeShiftChecklists = instance(),
            tickItem = instance(),
            plans = instance()
        )
    }
    bind<com.evoapps.evoshift.viewmodels.MyChecklistsViewModel>() with provider {
        com.evoapps.evoshift.viewmodels.MyChecklistsViewModel(instance(), instance(), instance(), instance())
    }
    // SH-170: the home screens' "Do zrobienia", the roster cards' trackers and the first steps.
    bind<com.evoapps.evoshift.viewmodels.TodoViewModel>() with provider {
        com.evoapps.evoshift.viewmodels.TodoViewModel(instance(), instance(), instance(), instance(), remindAvailability = instance(), plans = instance())
    }
    bind<com.evoapps.evoshift.viewmodels.RosterTrackerViewModel>() with provider {
        com.evoapps.evoshift.viewmodels.RosterTrackerViewModel(instance(), instance(), instance(), instance(), remindAvailability = instance(), plans = instance(), observeUnpublished = instance())
    }
    // SH-239: "Recommend EVoShift" (More, the card after joining) and the owner's "Invite the team" card.
    bind<com.evoapps.evoshift.viewmodels.RecommendPromptStore>() with singleton {
        com.evoapps.evoshift.viewmodels.RecommendPromptStore(instance())
    }
    bind<com.evoapps.evoshift.viewmodels.RecommendViewModel>() with provider {
        com.evoapps.evoshift.viewmodels.RecommendViewModel(instance(), instance(), instance())
    }
    bind<com.evoapps.evoshift.viewmodels.RecommendPromptViewModel>() with provider {
        com.evoapps.evoshift.viewmodels.RecommendPromptViewModel(instance(), instance())
    }
    bind<com.evoapps.evoshift.viewmodels.TeamInviteViewModel>() with provider {
        com.evoapps.evoshift.viewmodels.TeamInviteViewModel(instance(), instance(), instance(), instance(), plans = instance())
    }
    // SH-219: the owner's start path card (it took over SH-170's first steps).
    bind<com.evoapps.evoshift.viewmodels.StartPathViewModel>() with provider {
        com.evoapps.evoshift.viewmodels.StartPathViewModel(instance(), instance(), instance(), instance(), instance(), plans = instance())
    }
    // SH-134: the managers' "to review" list (Today at work, the daily records).
    bind<com.evoapps.evoshift.viewmodels.ClockReviewViewModel>() with provider {
        com.evoapps.evoshift.viewmodels.ClockReviewViewModel(
            instance(), instance(), instance(), instance(), instance(), instance(), instance(), plans = instance()
        )
    }
    bind<com.evoapps.evoshift.viewmodels.TodayChecklistsViewModel>() with provider {
        com.evoapps.evoshift.viewmodels.TodayChecklistsViewModel(instance(), instance(), instance(), plans = instance())
    }
    // SH-117: "For the accountant" on the Labour cost screen.
    bind<com.evoapps.evoshift.viewmodels.AccountantExportViewModel>() with factory { rosterId: String ->
        com.evoapps.evoshift.viewmodels.AccountantExportViewModel(
            rosterId = rosterId,
            exportForAccountant = instance(),
            getCurrentUser = instance(),
            observePlanActive = instance()
        )
    }

    bind<RosterCapacityViewModel>() with factory { rosterId: String ->
        RosterCapacityViewModel(
            rosterId = rosterId,
            getRoster = instance(),
            getCapacity = instance(),
            getCurrentUser = instance(),
            getAvailabilityRequest = instance(),
            remindAvailability = instance(),
            observePlanActive = instance()
        )
    }

    // SH-228: the sales statistics (money only, read by the view model's own gate).
    bind<com.evoapps.evoshift.viewmodels.StatisticsViewModel>() with factory { args: Route.Statistics ->
        com.evoapps.evoshift.viewmodels.StatisticsViewModel(
            workplaceId = args.workplaceId,
            anchor = args.anchor,
            getCurrentUserUseCase = instance(),
            workplaceRepository = instance(),
            getSalesComparison = instance(),
            observePlanActive = instance(),
            observeWeather = instance(),
            observeSpecialDays = instance()
        )
    }

    // SH-257: "Godziny pracy" (owners and managers; the view model's own gate).
    bind<com.evoapps.evoshift.viewmodels.WorkHoursViewModel>() with factory { args: Route.WorkHours ->
        com.evoapps.evoshift.viewmodels.WorkHoursViewModel(
            workplaceId = args.workplaceId,
            initialDate = args.date,
            getCurrentUserUseCase = instance(),
            workplaceRepository = instance(),
            shiftRepository = instance(),
            userRepository = instance(),
            confirmWorkHours = instance(),
            financialRepository = instance(),
            observePlanActive = instance(),
            recordActualWork = instance(),
            splitShift = instance(),
            undoShiftSplit = instance(),
            recordShiftWorker = instance()
        )
    }

    bind<DailyRecordsViewModel>() with factory { args: Route.DailyRecords ->
        DailyRecordsViewModel(
            workplaceId = args.workplaceId,
            rosterId = args.rosterId,
            workplaceRepository = instance(),
            rosterRepository = instance(),
            financialRepository = instance(),
            calculateDailyRecordUseCase = instance(),
            getCurrentUserUseCase = instance(),
            observePlanActive = instance(),
            shiftRepository = instance(),
            copyPlannedTimes = instance(),
            getDayClosures = instance(),
            getStaffingHints = instance(),
            recordShiftWorker = instance(),
            getAssignableEmployees = instance(),
            resolvePersonNames = instance(),
            saves = instance(),
            localSettings = instance(),
            observeWeather = instance(),
            observeSpecialDays = instance(),
            recordActualWork = instance(),
            splitShift = instance(),
            undoShiftSplit = instance()
        )
    }

    bind<WeeklyTemplatesViewModel>() with factory { args: Route.WeeklyTemplates ->
        WeeklyTemplatesViewModel(
            initialEditTemplateId = args.editTemplateId,
            openNew = args.openNew,
            getCurrentUserUseCase = instance(),
            getWeeklyAvailabilityTemplatesUseCase = instance(),
            saveWeeklyAvailabilityTemplateUseCase = instance(),
            deleteWeeklyAvailabilityTemplateUseCase = instance(),
            getWorkplaceUseCase = instance(),
            sessionManager = instance()
        )
    }

    bind<com.evoapps.evoshift.viewmodels.TipsViewModel>() with provider {
        com.evoapps.evoshift.viewmodels.TipsViewModel(
            tipRepository = instance(),
            previewTipSplit = instance(),
            saveTipPool = instance(),
            getCurrentUser = instance(),
            sessionManager = instance(),
            fileSaver = instance(),
            observePlanActive = instance()
        )
    }

    bind<com.evoapps.evoshift.viewmodels.LeaveBalancesViewModel>() with provider {
        com.evoapps.evoshift.viewmodels.LeaveBalancesViewModel(
            getTeamLeaveBalances = instance(),
            saveLeaveAllowance = instance(),
            getCurrentUser = instance(),
            sessionManager = instance(),
            fileSaver = instance(),
            observePlanActive = instance()
        )
    }

    // SH-123: Sales import (a file or the till).
    bind<com.evoapps.evoshift.viewmodels.SalesImportViewModel>() with provider {
        com.evoapps.evoshift.viewmodels.SalesImportViewModel(
            importDailySales = instance(),
            posIntegrations = instance(),
            workplaceRepository = instance(),
            getCurrentUser = instance(),
            sessionManager = instance(),
            observePlanActive = instance()
        )
    }

    bind<AbsencesViewModel>() with provider {
        AbsencesViewModel(
            absenceRepository = instance(),
            getCurrentUserUseCase = instance(),
            sessionManager = instance(),
            reportAbsenceUseCase = instance(),
            observePlanActive = instance(),
            getLeaveEntitlement = instance(),
            workRequestRepository = instance(),
            submitWorkRequest = instance(),
            workPlaces = instance(),
            workplaceRepository = instance(),
            userRepository = instance()
        )
    }

    bind<RequestsViewModel>() with provider {
        RequestsViewModel(
            absenceRepository = instance(),
            shiftRepository = instance(),
            rosterRepository = instance(),
            getCurrentUserUseCase = instance(),
            resolveAbsenceConflictsUseCase = instance(),
            approveSwapRequestUseCase = instance(),
            rejectSwapRequestUseCase = instance(),
            sessionManager = instance(),
            observePlanActive = instance(),
            getAssignableEmployees = instance(),
            observePendingRequests = instance(),
            undoAbsenceDecision = instance(),
            undoSwapApproval = instance(),
            workRequestRepository = instance(),
            decideWorkRequest = instance(),
            checkApprovalLimit = instance(),
            decideAbsence = instance()
        )
    }
}

/*
 * The app-wide view models that follow the session's workplace and month, built the same way for
 * the app (above) and for each window of its own (SH-161 F3, WindowModule.kt).
 */
internal fun DirectDIAware.newNotificationsViewModel() = NotificationsViewModel(instance(), instance())

internal fun DirectDIAware.newPendingRequestsViewModel() =
    com.evoapps.evoshift.viewmodels.PendingRequestsViewModel(instance(), instance(), instance())

internal fun DirectDIAware.newDashboardViewModel() = DashboardViewModel(
    getCurrentUserUseCase = instance(),
    getRosterSummaryUseCase = instance(),
    sendAvailabilityRequestUseCase = instance(),
    deleteRosterUseCase = instance(),
    sessionManager = instance(),
    observePlanActive = instance(),
    requestForMonth = instance()
)
